import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, MapPin, ChevronRight, ShoppingCart, ChevronDown, Wrench, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import HeaderSearch from './HeaderSearch';
import MobileSearch from './MobileSearch';

const EMAIL = "contact@atlantiquespaboutique.com";

const navLinks = [
  { href: "/blog", label: "Conseils" },
  { href: "/a-propos", label: "Notre Expertise" },
  { href: "/contact", label: "Contact" },
];

const spaSubLinks = [
  { href: "/spas-2-3-places", label: "Spas 2 à 3 places" },
  { href: "/spas-4-5-places", label: "Spas 4 à 5 places" },
  { href: "/spas-6-places-et-plus", label: "Spas 6 places et +" },
  { href: "/spas-debordement", label: "Spas à débordement" },
  { href: "/spas-de-nage", label: "Spas de nage" },
  { href: "/ice-bath", label: "ICE BATH" },
];

const piecesSubLinks = [
  { href: "/pieces-detachees", label: "Pièces détachées" },
  { href: "/accessoires-spa", label: "Accessoires" },
  { href: "/entretien-spa", label: "Entretien" },
  { href: "/filtres-spa", label: "Filtres spa" },
  { href: "/pompes-a-chaleur-spa", label: "Pompes à chaleur" },
  { href: "/couverture-spa-sur-mesure", label: "Couverture sur mesure" },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [spasMenuOpen, setSpasMenuOpen] = useState(false);
  const [piecesMenuOpen, setPiecesMenuOpen] = useState(false);
  const location = useLocation();
  
  // Refs for timeout management (smooth dropdown behavior)
  const spasTimeoutRef = useRef(null);
  const piecesTimeoutRef = useRef(null);
  
  // Smooth dropdown handlers with delay
  const handleSpasMouseEnter = () => {
    if (spasTimeoutRef.current) clearTimeout(spasTimeoutRef.current);
    setSpasMenuOpen(true);
  };
  
  const handleSpasMouseLeave = () => {
    spasTimeoutRef.current = setTimeout(() => {
      setSpasMenuOpen(false);
    }, 150); // 150ms delay before closing
  };
  
  const handlePiecesMouseEnter = () => {
    if (piecesTimeoutRef.current) clearTimeout(piecesTimeoutRef.current);
    setPiecesMenuOpen(true);
  };
  
  const handlePiecesMouseLeave = () => {
    piecesTimeoutRef.current = setTimeout(() => {
      setPiecesMenuOpen(false);
    }, 150); // 150ms delay before closing
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (spasTimeoutRef.current) clearTimeout(spasTimeoutRef.current);
      if (piecesTimeoutRef.current) clearTimeout(piecesTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Cart count management
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
      const count = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setCartCount(count);
    };
    
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    window.addEventListener('storage', updateCartCount);
    
    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  return (
    <>
      {/* Free shipping banner - France-wide delivery */}
      <div className="bg-emerald-600 text-white py-2 text-sm text-center">
        <span className="font-medium">🚚 Livraison France entière sur les pièces, accessoires et produits d'entretien – Offerte dès 80 € TTC</span>
      </div>

      {/* Top bar - Contact only */}
      <div className="bg-[#1E3A5F] text-white py-2.5 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center items-center">
          <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
            <Mail className="w-4 h-4" />
            <span>{EMAIL}</span>
          </a>
        </div>
      </div>

      {/* Main header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white border-b border-[#E2E8F0]'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group" data-testid="logo-link" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src="https://customer-assets.emergentagent.com/job_b492f36f-3806-46b5-8e70-81f70d5c3ec4/artifacts/gthr0fjv_refine-the-existing-logo---keep-the-geometric-lett.png"
                alt="Logo Atlantique Spa Boutique – spécialiste spa, pièces détachées et accessoires spa"
                className="h-10 md:h-12 w-auto object-contain"
                loading="eager"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center justify-center gap-2">
              {/* Nos Spas dropdown */}
              <div 
                className="relative"
                onMouseEnter={handleSpasMouseEnter}
                onMouseLeave={handleSpasMouseLeave}
              >
                <button
                  className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                    location.pathname.includes('/spas') || location.pathname === '/ice-bath'
                      ? 'text-[#1E3A5F] bg-[#1E3A5F]/10'
                      : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                  }`}
                  data-testid="nav-spas"
                >
                  Nos Spas
                  <ChevronDown className={`w-4 h-4 transition-transform ${spasMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown menu with buffer zone */}
                {spasMenuOpen && (
                  <div className="absolute top-full left-0 pt-1 w-56 z-50">
                    <div className="bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                      {spaSubLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            location.pathname === link.href
                              ? 'text-[#1E3A5F] bg-[#1E3A5F]/5 font-medium'
                              : 'text-slate-600 hover:text-[#1E3A5F] hover:bg-slate-50'
                          }`}
                          onClick={() => setSpasMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                      <div className="border-t border-slate-100 mt-2 pt-2">
                        <Link
                          to="/spas"
                          className="block px-4 py-2.5 text-sm text-[#F97316] hover:bg-orange-50 font-medium"
                          onClick={() => setSpasMenuOpen(false)}
                        >
                          Voir tous les spas →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Pièces & Accessoires dropdown */}
              <div 
                className="relative"
                onMouseEnter={handlePiecesMouseEnter}
                onMouseLeave={handlePiecesMouseLeave}
              >
                <button
                  className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                    location.pathname.includes('/pieces') || location.pathname.includes('/accessoires') || location.pathname.includes('/entretien') || location.pathname.includes('/pompes-a-chaleur')
                      ? 'text-[#1E3A5F] bg-[#1E3A5F]/10'
                      : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                  }`}
                  data-testid="nav-pieces"
                >
                  Pièces & Accessoires
                  <ChevronDown className={`w-4 h-4 transition-transform ${piecesMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown menu with buffer zone */}
                {piecesMenuOpen && (
                  <div className="absolute top-full left-0 pt-1 w-56 z-50">
                    <div className="bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                      {piecesSubLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            location.pathname === link.href
                              ? 'text-[#1E3A5F] bg-[#1E3A5F]/5 font-medium'
                              : 'text-slate-600 hover:text-[#1E3A5F] hover:bg-slate-50'
                          }`}
                          onClick={() => setPiecesMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                      <div className="border-t border-slate-100 mt-2 pt-2">
                        <Link
                          to="/pieces-accessoires"
                          className="block px-4 py-2.5 text-sm text-[#F97316] hover:bg-orange-50 font-medium"
                          onClick={() => setPiecesMenuOpen(false)}
                        >
                          Tout voir →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    location.pathname === link.href
                      ? 'text-[#1E3A5F] bg-[#1E3A5F]/10'
                      : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                  }`}
                  data-testid={`nav-${link.href.replace('/', '')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              {/* Global Search */}
              <HeaderSearch />
              
              {/* Cart icon */}
              <Link to="/panier" className="relative" data-testid="cart-btn">
                <Button variant="outline" size="sm" className="gap-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5 transition-all rounded-lg">
                  <ShoppingCart className="w-4 h-4" />
                  Panier
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#F97316] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>
              <Link to="/devis">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white btn-premium font-semibold rounded-lg" data-testid="devis-btn">
                  Devis Gratuit
                </Button>
              </Link>
            </div>

            {/* Mobile buttons */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Search Button */}
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="p-2 text-[#475569] hover:text-[#1E3A5F] hover:bg-[#F8FAFC] rounded-lg transition-colors"
                aria-label="Rechercher"
                data-testid="mobile-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>
              
              {/* Mobile Cart */}
              <Link to="/panier" className="relative p-2 text-[#475569] hover:text-[#1E3A5F] hover:bg-[#F8FAFC] rounded-lg transition-colors" data-testid="mobile-cart-btn">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#F97316] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-[#F8FAFC]" data-testid="mobile-menu-btn">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 p-0 bg-white">
                  <div className="flex flex-col h-full">
                    {/* Menu Header */}
                    <div className="p-6 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1E3A5F] rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-lg">AS</span>
                        </div>
                        <div>
                          <div className="font-bold text-[#0F172A]">Atlantique Spa Boutique</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu Links */}
                    <nav className="flex-1 p-4 overflow-y-auto">
                      <div className="space-y-1">
                        {/* Nos Spas section with sub-links */}
                        <div className="mb-2">
                          <div className="px-4 py-3 text-base font-semibold text-[#0F172A]">
                            Nos Spas
                          </div>
                          <div className="pl-4 space-y-1">
                            {spaSubLinks.map((link) => (
                              <Link
                                key={link.href}
                                to={link.href}
                                className={`flex items-center justify-between px-4 py-2.5 text-sm rounded-lg transition-all ${
                                  location.pathname === link.href
                                    ? 'text-[#1E3A5F] bg-[#1E3A5F]/10 font-medium'
                                    : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                                }`}
                              >
                                {link.label}
                                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                              </Link>
                            ))}
                            <Link
                              to="/spas"
                              className="flex items-center justify-between px-4 py-2.5 text-sm rounded-lg text-[#F97316] hover:bg-orange-50 font-medium"
                            >
                              Voir tous les spas
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>

                        {/* Pièces & Accessoires section with sub-links */}
                        <div className="mb-2">
                          <div className="px-4 py-3 text-base font-semibold text-[#0F172A]">
                            Pièces & Accessoires
                          </div>
                          <div className="pl-4 space-y-1">
                            {piecesSubLinks.map((link) => (
                              <Link
                                key={link.href}
                                to={link.href}
                                className={`flex items-center justify-between px-4 py-2.5 text-sm rounded-lg transition-all ${
                                  location.pathname === link.href
                                    ? 'text-[#1E3A5F] bg-[#1E3A5F]/10 font-medium'
                                    : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                                }`}
                              >
                                {link.label}
                                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                              </Link>
                            ))}
                            <Link
                              to="/pieces-accessoires"
                              className="flex items-center justify-between px-4 py-2.5 text-sm rounded-lg text-[#F97316] hover:bg-orange-50 font-medium"
                            >
                              Tout voir
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                        
                        {/* Other nav links */}
                        {navLinks.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            className={`flex items-center justify-between px-4 py-3.5 text-base font-medium rounded-lg transition-all ${
                              location.pathname === link.href
                                ? 'text-[#1E3A5F] bg-[#1E3A5F]/10'
                                : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                            }`}
                          >
                            {link.label}
                            <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                          </Link>
                        ))}
                      </div>
                    </nav>
                    
                    {/* Menu Footer */}
                    <div className="p-4 border-t border-[#E2E8F0] bg-[#F8FAFC] space-y-3">
                      <Link to="/devis" className="block">
                        <Button className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold h-12 rounded-lg">
                          Demander un Devis
                        </Button>
                      </Link>
                      <Link to="/contact" className="block">
                        <Button variant="outline" className="w-full gap-2 h-11 border-[#1E3A5F] text-[#1E3A5F] rounded-lg">
                          Nous contacter
                        </Button>
                      </Link>
                      <p className="text-xs text-center text-[#64748B] pt-2">
                        Intervention : 44 • 85 • 56 • 49
                      </p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Search Overlay */}
      <MobileSearch isOpen={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)} />
    </>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0F172A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-6" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src="https://customer-assets.emergentagent.com/job_b492f36f-3806-46b5-8e70-81f70d5c3ec4/artifacts/gthr0fjv_refine-the-existing-logo---keep-the-geometric-lett.png"
                alt="Logo Atlantique Spa Boutique – spécialiste spa, pièces détachées et accessoires spa"
                className="h-12 w-auto object-contain"
                loading="lazy"
              />
            </Link>
            <p className="text-[#94A3B8] text-base leading-relaxed mb-5">
              Spécialiste du spa : conseil, vente et installation. SAV local. Accompagnement durable.
            </p>
            <p className="text-sm text-[#64748B] leading-relaxed">
              📦 <strong>Pièces & accessoires :</strong> Livraison France entière<br/>
              🏠 <strong>Spas :</strong> Installation en Loire-Atlantique, Vendée, Morbihan et Maine-et-Loire
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold mb-6 text-white text-lg">Navigation</h4>
            <ul className="space-y-4">
              <li><Link to="/spas" className="text-[#94A3B8] hover:text-white transition-colors text-base">Nos Spas</Link></li>
              <li><Link to="/pieces-accessoires" className="text-[#94A3B8] hover:text-white transition-colors text-base">Pièces & Accessoires</Link></li>
              <li className="pl-4 space-y-2">
                <Link to="/pieces-detachees" className="text-[#64748B] hover:text-white transition-colors text-sm block">→ Pièces détachées</Link>
                <Link to="/accessoires-spa" className="text-[#64748B] hover:text-white transition-colors text-sm block">→ Accessoires spa</Link>
                <Link to="/entretien-spa" className="text-[#64748B] hover:text-white transition-colors text-sm block">→ Entretien spa</Link>
                <Link to="/pompes-a-chaleur-spa" className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm block flex items-center gap-1">
                  → Pompes à chaleur <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                </Link>
              </li>
              <li><Link to="/remplacement-pompe-spa-nantes" className="text-[#F97316] hover:text-orange-300 transition-colors text-base font-medium">Réparation spa Nantes</Link></li>
              <li><Link to="/blog" className="text-[#94A3B8] hover:text-white transition-colors text-base">Conseils</Link></li>
              <li><Link to="/a-propos" className="text-[#94A3B8] hover:text-white transition-colors text-base">Notre Expertise</Link></li>
              <li><Link to="/faq" className="text-[#94A3B8] hover:text-white transition-colors text-base">FAQ</Link></li>
              <li><Link to="/garanties-engagements" className="text-[#94A3B8] hover:text-white transition-colors text-base">Garanties & Engagements</Link></li>
            </ul>
          </div>

          {/* Zone d'intervention - SPAS UNIQUEMENT */}
          <div>
            <h4 className="font-semibold mb-4 text-white text-lg">Zone locale (Spas)</h4>
            <p className="text-[#64748B] text-xs mb-4">Livraison, installation & SAV spas</p>
            <ul className="space-y-3 text-[#94A3B8] text-base">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#F97316] rounded-full"></span>
                Loire-Atlantique (44)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#F97316] rounded-full"></span>
                Vendée (85)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#F97316] rounded-full"></span>
                Morbihan (56)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#F97316] rounded-full"></span>
                Maine-et-Loire (49)
              </li>
            </ul>
            <p className="text-[#64748B] text-xs mt-4">📦 Pièces & accessoires : France entière</p>
            <div className="mt-4">
              <Link to="/spa-nantes" className="text-[#F97316] hover:text-orange-300 text-base font-medium inline-flex items-center gap-1">
                Spa Nantes <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-6 text-white text-lg">Contact</h4>
            <div className="space-y-4">
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-white hover:text-[#F97316] transition-colors group">
                <div className="w-11 h-11 bg-[#1E3A5F] rounded-lg flex items-center justify-center group-hover:bg-[#F97316] transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="text-sm break-all">{EMAIL}</span>
              </a>
            </div>
            <div className="mt-8 space-y-3">
              <Link to="/devis">
                <Button className="w-full bg-[#F97316] hover:bg-[#EA580C] font-semibold h-12 rounded-lg text-base" data-testid="footer-devis-btn">
                  Devis Gratuit
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 font-semibold h-11 rounded-lg text-base">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-[#1E3A5F] mt-14 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-[#64748B] text-base">
              © {new Date().getFullYear()} Atlantique Spa Boutique. Tous droits réservés.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#64748B]">
              <Link to="/cgv" className="hover:text-white transition-colors">Conditions Générales de Vente</Link>
              <Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
              <a href="https://www.atlantiquespaservice.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1">
                <Wrench className="w-3 h-3" />
                Réparation / SAV spa (site officiel)
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Schema.org LocalBusiness */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Atlantique Spa Boutique",
          "description": "Spécialiste du spa : conseil, vente et installation. SAV local et accompagnement durable à Nantes et Grand Ouest.",
          "email": "contact@atlantiquespaboutique.com",
          "areaServed": ["Nantes", "Loire-Atlantique", "Vendée", "Morbihan", "Maine-et-Loire"],
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Nantes",
            "addressRegion": "Pays de la Loire",
            "addressCountry": "FR"
          },
          "openingHours": "Mo-Sa 09:00-19:00",
          "priceRange": "€€€"
        })
      }} />
    </footer>
  );
};

// Mobile sticky CTA - Email only (no phone)
export const MobileCTA = () => {
  return (
    <div className="sticky-mobile-cta md:hidden" data-testid="mobile-sticky-cta">
      <a href={`mailto:${EMAIL}`} className="flex-1">
        <Button className="w-full bg-slate-900 hover:bg-slate-800 gap-2 h-12 font-semibold">
          <Mail className="w-4 h-4" />
          Email
        </Button>
      </a>
      <Link to="/devis" className="flex-1">
        <Button className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white gap-2 h-12 font-semibold">
          Devis Gratuit
        </Button>
      </Link>
    </div>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 page-content">
        {children}
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default Layout;
